/**
 * Export utilities for roof safety data
 * Supports CSV and PDF export formats
 */

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

/**
 * Export equipment data to CSV
 */
export function exportToCSV(data, equipmentType, propertyName) {
  const headers = [
    'ID',
    'Name/Reference',
    'Location',
    'Type',
    'Status', 
    'Test Status',
    'Installation Date',
    'Last Inspection',
    'Next Inspection',
    'Installer',
    'Batch Reference',
    'NFC Tag',
    'RFID Tag',
    'Notes'
  ];

  // Convert data to CSV format
  const csvData = data.map(item => [
    item.id || '',
    item.name || item.systemName || item.reference || '',
    item.location || item.location_description || '',
    item.type || '',
    item.status || '',
    item.testStatus || 'Unknown',
    formatDate(item.installationDate || item.date_installed) || '',
    formatDate(item.lastTestDate || item.last_inspection_date) || '',
    formatDate(item.nextTestDate || item.next_inspection_date) || '',
    item.installedBy || item.installed_by || '',
    item.batchReference || item.batch_reference || '',
    item.nfcTagId || item.nfc_tag_id || '',
    item.rfidTagId || item.rfid_tag_id || '',
    (item.notes || '').replace(/"/g, '""') // Escape quotes in notes
  ]);

  // Combine headers and data
  const csvContent = [headers, ...csvData]
    .map(row => row.map(field => `"${field}"`).join(','))
    .join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const filename = `${propertyName}_${equipmentType}_${formatDate(new Date())}.csv`;
  
  if (navigator.msSaveBlob) {
    // IE10+
    navigator.msSaveBlob(blob, filename);
  } else {
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Export equipment data to PDF
 */
export function exportToPDF(data, equipmentType, propertyName, filters = {}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Title
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('Roof Safety Equipment Report', 20, 25);
  
  // Property and date info
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text(`Property: ${propertyName}`, 20, 40);
  doc.text(`Equipment Type: ${equipmentType}`, 20, 50);
  doc.text(`Generated: ${formatDateTime(new Date())}`, 20, 60);
  doc.text(`Total Items: ${data.length}`, 20, 70);

  // Filters applied
  if (hasActiveFilters(filters)) {
    doc.text('Filters Applied:', 20, 85);
    let yPos = 95;
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== '') {
        doc.setFontSize(10);
        doc.text(`• ${formatFilterKey(key)}: ${value}`, 25, yPos);
        yPos += 10;
      }
    });
  }

  // Summary statistics
  const stats = calculateStats(data);
  const summaryY = hasActiveFilters(filters) ? 125 : 90;
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Summary', 20, summaryY);
  
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  doc.text(`Pass: ${stats.pass}`, 20, summaryY + 15);
  doc.text(`Fail: ${stats.fail}`, 70, summaryY + 15);
  doc.text(`Pending: ${stats.pending}`, 120, summaryY + 15);
  doc.text(`Unknown: ${stats.unknown}`, 170, summaryY + 15);
  
  doc.text(`Overdue: ${stats.overdue}`, 20, summaryY + 30);
  doc.text(`Due Soon: ${stats.dueSoon}`, 70, summaryY + 30);

  // Equipment table
  const tableData = data.map(item => [
    item.id || '',
    (item.name || item.systemName || '').substring(0, 20) + 
      ((item.name || item.systemName || '').length > 20 ? '...' : ''),
    (item.location || '').substring(0, 25) + 
      ((item.location || '').length > 25 ? '...' : ''),
    item.type || '',
    item.status || '',
    formatDate(item.lastTestDate || item.last_inspection_date) || '',
    formatDate(item.nextTestDate || item.next_inspection_date) || ''
  ]);

  const tableHeaders = [
    'ID', 'Name', 'Location', 'Type', 'Status', 'Last Test', 'Next Test'
  ];

  doc.autoTable({
    head: [tableHeaders],
    body: tableData,
    startY: summaryY + 45,
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    headStyles: {
      fillColor: [59, 130, 246],
      textColor: 255,
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 15 }, // ID
      1: { cellWidth: 35 }, // Name
      2: { cellWidth: 35 }, // Location
      3: { cellWidth: 25 }, // Type
      4: { cellWidth: 20 }, // Status
      5: { cellWidth: 25 }, // Last Test
      6: { cellWidth: 25 }  // Next Test
    },
    didDrawCell: function(data) {
      // Color code status cells
      if (data.column.index === 4 && data.cell.section === 'body') {
        const status = data.cell.text[0].toLowerCase();
        if (status === 'fail' || status === 'failed') {
          doc.setFillColor(239, 68, 68);
        } else if (status === 'pass' || status === 'passed') {
          doc.setFillColor(34, 197, 94);
        } else if (status === 'pending') {
          doc.setFillColor(245, 158, 11);
        }
      }
    }
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, doc.internal.pageSize.height - 10);
    doc.text('CloudSafe Roof Safety Report', 20, doc.internal.pageSize.height - 10);
  }

  // Save the PDF
  const filename = `${propertyName}_${equipmentType}_Report_${formatDate(new Date())}.pdf`;
  doc.save(filename);
}

/**
 * Helper functions
 */
function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB');
}

function formatDateTime(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
}

function hasActiveFilters(filters) {
  return Object.values(filters).some(value => 
    value && value !== 'All' && value !== ''
  );
}

function formatFilterKey(key) {
  const keyMap = {
    status: 'Status',
    type: 'Type',
    testResult: 'Test Result',
    priority: 'Priority',
    dateFrom: 'Date From',
    dateTo: 'Date To',
    location: 'Location'
  };
  return keyMap[key] || key;
}

function calculateStats(data) {
  const stats = {
    pass: 0,
    fail: 0,
    pending: 0,
    unknown: 0,
    overdue: 0,
    dueSoon: 0
  };

  const today = new Date();
  const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));

  data.forEach(item => {
    const status = (item.status || 'unknown').toLowerCase();
    if (status.includes('pass')) stats.pass++;
    else if (status.includes('fail')) stats.fail++;
    else if (status.includes('pending')) stats.pending++;
    else stats.unknown++;

    // Check if overdue or due soon
    const nextTestDate = new Date(item.nextTestDate || item.next_inspection_date);
    if (!isNaN(nextTestDate.getTime())) {
      if (nextTestDate < today) {
        stats.overdue++;
      } else if (nextTestDate <= thirtyDaysFromNow) {
        stats.dueSoon++;
      }
    }
  });

  return stats;
}

export default {
  exportToCSV,
  exportToPDF
};
